import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { CameraAngles, inferSwingPosition } from '@core';
import { useTranslate } from '@i18n';
import { useUiParameters } from '@queries';
import { layoutCustomizationsAtom } from '@store';

import { swingImagesAtom } from '../../store/swing';

import { Parameters, PositionImage } from './components';
import * as css from './SwingFoundations.css';

type SwingFoundationsProps = {
    nodeId: string;
};

export const SwingFoundations = ({ nodeId }: SwingFoundationsProps) => {
    const t = useTranslate();
    const customizations = useAtomValue(layoutCustomizationsAtom);
    const { data: parameters } = useUiParameters();
    const images = useAtomValue(swingImagesAtom);

    const paramMap = useMemo(
        () => Object.fromEntries(parameters?.map((param) => [param.id, param] as const) || []),
        [parameters],
    );
    const nodes = useMemo(
        () => customizations?.filter((node) => node.parentUiNodeId === nodeId && node.showChildOn?.includes('floor')),
        [customizations, nodeId],
    );
    const parameterNodes = useMemo(
        () =>
            nodes?.flatMap((node) => {
                const param = paramMap[node.childUiNodeId];
                return param ? [param] : [];
            }),
        [nodes, paramMap],
    );

    const position = inferSwingPosition(nodeId);
    const title = (position && t(`swing_position.${position}`)) || position?.toUpperCase();
    const subtitle = t('module_type.swing_foundations');
    const angle: CameraAngles = position === 'p2' ? 'down_the_line' : 'face_on';

    return (
        <div className={css.root}>
            <div className={css.top}>
                <h3 className={css.title}>{title}</h3>
                <p className={css.subtitle}>{subtitle}</p>
            </div>
            <div className={css.body}>
                <PositionImage images={images} angle={angle} position={position} />
                <Parameters nodes={parameterNodes} />
            </div>
        </div>
    );
};
